import type { UserModule } from '../types';
import Aura from '@openvue/themes/aura';
import PrimeVue, { type PrimeVueLocaleOptions } from 'openvue/config';
import ToastService from 'openvue/toastservice';
import ConfirmationService from 'openvue/confirmationservice';
import { definePreset } from '@openvue/themes';
import { i18n } from './i18n';

export const install: UserModule = ({ app }) => {
    const primeLocalesFiles = import.meta.glob<{
        [lang: string]: PrimeVueLocaleOptions;
    }>('../locales/_openvue/*.json', { eager: true });

    const locale = <WritableComputedRef<string>>i18n.global.locale;
    const currentLocale = locale.value;
    const primeLocalesFile = Object.keys(primeLocalesFiles).find((file) =>
        file.endsWith('/' + currentLocale + '.json')
    );
    const primeLocalesJson = primeLocalesFile
        ? primeLocalesFiles[primeLocalesFile]
        : {};
    const primeLocales = primeLocalesJson
        ? primeLocalesJson[currentLocale]
        : {};

    const preset = definePreset(Aura, {
        primitive: {
            red: {
                50: '#FFEEEB',
                100: '#FFE1DB',
                200: '#FFBEB3',
                300: '#FEA08F',
                400: '#FE7D67',
                500: '#FE5E41',
                600: '#FE2701',
                700: '#C11E01',
                800: '#7F1401',
                900: '#420A00',
                950: '#1E0500'
            },
            green: {
                50: '#F2F8EC',
                100: '#E3F0D6',
                200: '#C9E2B1',
                300: '#ACD388',
                400: '#92C563',
                500: '#76B041',
                600: '#5F8E34',
                700: '#466826',
                800: '#30471A',
                900: '#17220C',
                950: '#0D1307'
            },
            gray: {
                50: '#FCFCFC',
                100: '#F7F7F7',
                200: '#F2F2F2',
                300: '#EBEBEB',
                400: '#E3E3E3',
                500: '#DDDDDD',
                600: '#B0B0B0',
                700: '#858585',
                800: '#595959',
                900: '#2B2B2B',
                950: '#171717'
            },
            blue: {
                50: '#EEF6FC',
                100: '#DDEEF8',
                200: '#BADDF2',
                300: '#98CCEB',
                400: '#76BAE5',
                500: '#52A8DE',
                600: '#278ECE',
                700: '#1D6A9A',
                800: '#144767',
                900: '#0A2333',
                950: '#05121A'
            }
        },
        components: {
            progressspinner: {
                colorScheme: {
                    dark: {
                        root: {
                            colorOne: '{sky.200}',
                            colorTwo: '{sky.500}',
                            colorThree: '{sky.600}',
                            colorFour: '{sky.900}'
                        }
                    },
                    light: {
                        root: {
                            colorOne: '{sky.200}',
                            colorTwo: '{sky.500}',
                            colorThree: '{sky.600}',
                            colorFour: '{sky.900}'
                        }
                    }
                }
            }
        },
        semantic: {
            surface: {
                50: '#FCFCFC',
                100: '#F7F7F7',
                200: '#F2F2F2',
                300: '#EBEBEB',
                400: '#E3E3E3',
                500: '#DDDDDD',
                600: '#B0B0B0',
                700: '#858585',
                800: '#595959',
                900: '#2B2B2B',
                950: '#171717'
            },
            colorScheme: {
                light: {
                    primary: {
                        50: '#E4EBF7',
                        100: '#CCD9F0',
                        200: '#95B0DF',
                        300: '#628AD0',
                        400: '#3867B8',
                        500: '#284984',
                        600: '#203B6A',
                        700: '#182C4E',
                        800: '#0F1C33',
                        900: '#080F1B',
                        950: '#04070C',
                        color: '{primary.500}'
                    },
                    secondary: {
                        50: '#EEF6FC',
                        100: '#DDEEF8',
                        200: '#BADDF2',
                        300: '#98CCEB',
                        400: '#76BAE5',
                        500: '#52A8DE',
                        600: '#278ECE',
                        700: '#1D6A9A',
                        800: '#144767',
                        900: '#0A2333',
                        950: '#05121A'
                    },
                    info: {
                        50: '#E4EBF7',
                        100: '#CCD9F0',
                        200: '#95B0DF',
                        300: '#628AD0',
                        400: '#3867B8',
                        500: '#284984',
                        600: '#203B6A',
                        700: '#182C4E',
                        800: '#0F1C33',
                        900: '#080F1B',
                        950: '#04070C'
                    }
                },
                dark: {
                    primary: {
                        50: '#EEF6FC',
                        100: '#DDEEF8',
                        200: '#BADDF2',
                        300: '#98CCEB',
                        400: '#76BAE5',
                        500: '#52A8DE',
                        600: '#278ECE',
                        700: '#1D6A9A',
                        800: '#144767',
                        900: '#0A2333',
                        950: '#05121A'
                    },
                    secondary: {
                        50: '#FCFCFC',
                        100: '#F7F7F7',
                        200: '#F2F2F2',
                        300: '#EBEBEB',
                        400: '#E3E3E3',
                        500: '#DDDDDD',
                        600: '#B0B0B0',
                        700: '#858585',
                        800: '#595959',
                        900: '#2B2B2B',
                        950: '#171717'
                    },
                    info: {
                        50: '#EEF6FC',
                        100: '#DDEEF8',
                        200: '#BADDF2',
                        300: '#98CCEB',
                        400: '#76BAE5',
                        500: '#52A8DE',
                        600: '#278ECE',
                        700: '#1D6A9A',
                        800: '#144767',
                        900: '#0A2333',
                        950: '#05121A'
                    }
                }
            }
        }
    });

    app.use(PrimeVue, {
        ripple: true,
        locale: primeLocales,
        theme: {
            preset,
            options: {
                darkModeSelector: '.dark',
                cssLayer: {
                    name: 'primevue',
                    order: 'theme, base, primevue'
                }
            }
        }
    });
    app.use(ToastService);
    app.use(ConfirmationService);
};
